import type { CityConfig } from "../city-template/types";

export const carlsbadConfig: CityConfig = {
  city: "Carlsbad",
  state: "California",
  stateAbbr: "CA",
  slug: "carlsbad",
  route: "/cities/carlsbad",
  title: "Emergency Passport Carlsbad, CA | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Carlsbad, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for North San Diego County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Carlsbad, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Carlsbad and North San Diego County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Carlsbad%20California%20coastal%20village%20at%20golden%20hour%20with%20sandy%20beach%2C%20palm%20trees%2C%20and%20charming%20downtown%20village%20shops%2C%20Pacific%20Ocean%20waves%20in%20the%20background%2C%20warm%20sunset%20light%20with%20amber%20and%20orange%20tones%2C%20colorful%20flowers%20and%20coastal%20landscaping%2C%20serene%20seaside%20town%20atmosphere%2C%20professional%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20beautiful%20coastal%20scene&width=1800&height=700&seq=carlsbad-hero-001&orientation=landscape",
  heroImageAlt: "Carlsbad California coastal village at golden hour with sandy beach and charming downtown",
  geoPosition: "33.1581;-117.3506",
  geoPlacename: "Carlsbad, California",
  regionalAgency: {
    name: "San Diego Passport Agency",
    address: "401 W A St, Suite 1300, San Diego, CA 92101",
    distance: "Located in downtown San Diego — approximately 35–45 minutes from Carlsbad via I-5",
  },
  nearbyCitiesZips: [
    { name: "Carlsbad", zips: "92008, 92009, 92010, 92011" },
    { name: "Oceanside", zips: "92054, 92056, 92057" },
    { name: "Vista", zips: "92081, 92083, 92084" },
    { name: "San Marcos", zips: "92069, 92078, 92096" },
    { name: "Encinitas", zips: "92024, 92007" },
    { name: "Escondido", zips: "92025, 92026, 92027, 92029" },
    { name: "Del Mar", zips: "92014" },
    { name: "Solana Beach", zips: "92075" },
    { name: "Rancho Santa Fe", zips: "92067, 92091" },
    { name: "Fallbrook", zips: "92028" },
    { name: "Bonsall", zips: "92003" },
    { name: "Leucadia", zips: "92024" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent North County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in San Diego", route: "/cities/san-diego" },
    { name: "Emergency passport help near Oceanside", route: "/cities/oceanside" },
    { name: "Urgent passport services near Escondido", route: "/cities/escondido" },
    { name: "Expedited passport services in Chula Vista", route: "/cities/chula-vista" },
    { name: "Same-day passport help in Los Angeles", route: "/cities/los-angeles" },
    { name: "Rush passport services in Irvine", route: "/cities/irvine" },
    { name: "Passport courier services near Santa Ana", route: "/cities/santa-ana" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Carlsbad remotely via secure courier. We do not have a walk-in office in Carlsbad — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for North San Diego County travelers.",
  fpcOfficeDistance: "Serving all of North San Diego County",
  serviceAreaIntro: "From the Carlsbad Village to La Costa, from Bressi Ranch to the Aviara neighborhood — Fast Passport Center serves the entire Carlsbad community. We regularly help travelers from Oceanside, Vista, San Marcos, and Encinitas get their passports fast.",
  nearbyCitiesIntro: "We serve all Carlsbad neighborhoods and nearby North San Diego County cities including Oceanside, Vista, San Marcos, Encinitas, Del Mar, and Solana Beach. If you are in North San Diego County, we have you covered.",
  howItWorksIntro: "Most Carlsbad travelers complete everything online and ship documents overnight. No need to drive to downtown San Diego or wait in line at the regional agency.",
  mistakesIntro: "With Palomar Airport and San Diego International both serving Carlsbad travelers, North County residents can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Carlsbad and North San Diego County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Carlsbad? Here are the most common ones we hear from North San Diego County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent North County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michelle D.",
      location: "Carlsbad, CA",
      rating: 5,
      text: "My family trip to Cabo was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Saved our vacation completely.",
      service: "Emergency Passport",
    },
    {
      name: "Robert K.",
      location: "Oceanside, CA",
      rating: 5,
      text: "Needed to renew for a surf trip to Bali. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer T.",
      location: "Vista, CA",
      rating: 5,
      text: "Lost my passport a week before my business trip to Tokyo. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "David L.",
      location: "Encinitas, CA",
      rating: 5,
      text: "As a frequent international traveler from North County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "carlsbad-1",
      name: "Carlsbad Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2785 Roosevelt St",
      city: "Carlsbad",
      state: "CA",
      zip: "92008",
      phone: "(760) 434-4333",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Carlsbad Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "carlsbad-2",
      name: "Oceanside Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1604 S El Camino Real",
      city: "Oceanside",
      state: "CA",
      zip: "92054",
      phone: "(760) 433-3310",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.5 miles from Carlsbad",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "carlsbad-3",
      name: "Vista Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 E Bobier Dr",
      city: "Vista",
      state: "CA",
      zip: "92084",
      phone: "(760) 726-4421",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6.2 miles from Carlsbad",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for inland North County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "carlsbad-4",
      name: "San Marcos Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1030 La Bonita Dr",
      city: "San Marcos",
      state: "CA",
      zip: "92078",
      phone: "(760) 744-0780",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.0 miles from Carlsbad",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for inland North County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "carlsbad-5",
      name: "Encinitas Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "180 N El Camino Real",
      city: "Encinitas",
      state: "CA",
      zip: "92024",
      phone: "(760) 753-4357",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.2 miles from Carlsbad",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for coastal North County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "carlsbad-6",
      name: "Escondido City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "201 N Broadway",
      city: "Escondido",
      state: "CA",
      zip: "92025",
      phone: "(760) 839-4617",
      hours: "Mon–Thu: 7:30 AM – 5:30 PM, Fri: 8:00 AM – 5:00 PM",
      distance: "12 miles from Carlsbad",
      website: "https://www.escondido.org/city-clerk",
      websiteLabel: "Escondido City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Diego", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "North County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at North County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many North County applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Diego regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many North County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "North County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Carlsbad." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Carlsbad address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Carlsbad before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Carlsbad for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most North San Diego County travelers." },
    { q: "How fast can I renew my passport in Carlsbad?", a: "Fast Passport Center offers fast passport renewal in Carlsbad in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Carlsbad in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Carlsbad?", a: "Same-day passport appointments are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Carlsbad residents." },
    { q: "Can I expedite a child passport in Carlsbad?", a: "Yes. Fast Passport Center expedites child passports in Carlsbad for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk North San Diego County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Carlsbad with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Carlsbad?",
      a: "With Fast Passport Center's emergency courier service, Carlsbad travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Diego Passport Agency downtown?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Carlsbad?",
      a: "Popular options include the Carlsbad Post Office (92008), Oceanside Main Post Office (92054), Vista Post Office (92084), and Encinitas Post Office (92024). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Carlsbad?",
      a: "Same-day passports are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in South Carlsbad or nearby beach communities?",
      a: "We serve all of North San Diego County including La Costa, Aviara, Leucadia, Cardiff, Solana Beach, and Del Mar. Our overnight shipping labels work from any UPS location, so your location within the area doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Carlsbad?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Carlsbad?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Carlsbad and Oceanside offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Carlsbad?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Carlsbad?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk North San Diego County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Carlsbad", address: "2510 El Camino Real, Carlsbad, CA 92008", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Oceanside", address: "100 S El Camino Real, Oceanside, CA 92054", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Carlsbad", address: "2621 El Camino Real, Carlsbad, CA 92008", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Encinitas", address: "119 N El Camino Real, Encinitas, CA 92024", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Vista", address: "1600 S Melrose Dr, Vista, CA 92081", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — San Marcos", address: "555 Grand Ave, San Marcos, CA 92069", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Carlsbad", address: "2675 Gateway Rd, Carlsbad, CA 92009", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — San Marcos", address: "1215 N El Camino Real, San Marcos, CA 92069", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Carlsbad",
      address: "2905 Oceanside Blvd, Oceanside, CA 92054",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — San Marcos",
      address: "590 W San Marcos Blvd, San Marcos, CA 92069",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Vista",
      address: "1901 E Vista Way, Vista, CA 92084",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Oceanside",
      address: "2216 El Camino Real, Oceanside, CA 92054",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for coastal North County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "CLD",
      name: "McClellan-Palomar Airport",
      distance: "3 miles from downtown Carlsbad",
      address: "2198 Palomar Airport Rd, Carlsbad, CA 92011",
      note: "Regional airport serving North San Diego County with domestic flights and some international connections.",
      flightUrl: "https://www.sdcounty.ca.gov/general/airports.html",
      flightLabel: "Palomar Airport",
      featured: true,
    },
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "35 miles from Carlsbad",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "Primary international airport for San Diego County. Major hub for international flights to Asia, Europe, and Latin America.",
      flightUrl: "https://www.san.org/",
      flightLabel: "San Diego International",
      featured: false,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "45 miles from Carlsbad",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "Alternative for international flights via Orange County. Accessible via I-5 south.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
  ],
};